import React, { useMemo } from 'react';
import { connect } from "react-redux";
import {
  SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS,
  SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS,
  SIDEBAR_NAVIGATION_BATHROOM_CABINET_BUTTONS,
  SIDEBAR_NAVIGATION_CABINET_BUTTONS,
} from "../../../../constants";
import { injectIntl } from "react-intl";
import $ from "jquery";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import {
  addChangeValuePopup,
  setSQFTActiveButton,
  setSQFTMenuOpened
} from "../../../../../../actions/sqftMenuFunctions";
import config from "../../../canvas/store/sqftConfig";
import {
  handleResetClick,
  handleResizeClick,
  handleRotateClick,
  handleSpacerClick,
  handleWallClick,
  is3DMode,
  resetEvents
} from "../../NavigationHelper";
import useFetchFeatures from "../../../../hocs/useFetchFeatures";
import LoadingSpinner from "../../../../../ui/loadingSpinner";
import { getIsBathroom } from "../../../../../kvComponent/navigation/countertopSwitchers";
import { getCurrentLayout } from "../../../../../appLayout";
import { getFeatureGroupName } from "../../../../../../misc/helpers";
import {
  hideSqftTooltip,
  showSqftTooltip
} from "../../../../../../actions/sqftTooltipFunctions";

/**
 * Dependency ids are used for:
 * 1) Fetching features (we will read only those deps that exist in featuresData)
 * 2) Wizard gating (if wizard is active, at least one wizardSelectedValues.partId must match deps)
 */
const CABINET_MENU_BUTTON_DEPS = {
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.lower]: ["bottom_cabinet", "center_island_cabinet"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.upper]: ["top_cabinet"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.tall]: ["tall_cabinet"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.centerIsland]: ["center_island_countertop", "countertop"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.handles]: ["handle", "top_handle", "bottom_handle"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.hinge]: ["hinge"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.crown_molding]: ["crown_molding"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.end_panel]: ["wall_door_end_accessory", "base_door", "island_panel", "refridgerator_panel"],

  // 3D cabinet buttons
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.bottom_cabinet]: ["bottom_cabinet"],
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.top_cabinet]: ["top_cabinet"],
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.tall_cabinet]: ["tall_cabinet"],

  // 3D menu can also show handles/centerIsland
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.handles]: ["handle", "top_handle", "bottom_handle"],
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.centerIsland]: ["center_island_countertop", "countertop"],

};

/**
 * Collect all unique deps to fetch once.
 * useFetchFeatures will return featuresData; we will only read keys that exist.
 */
const CABINET_MENU_FETCH_KEYS = Array.from(
  new Set(Object.values(CABINET_MENU_BUTTON_DEPS).flat())
);


const normalizeWizardSelectedValues = (wizardSelectedValues) => {
  if (!wizardSelectedValues) return [];
  if (Array.isArray(wizardSelectedValues)) return wizardSelectedValues;
  if (typeof wizardSelectedValues === "object") return Object.values(wizardSelectedValues);
  return [];
};

/**
 * Check if wizard has at least one selected value matching allowed partIds.
 */
const hasWizardMatch = (wizardSelectedValues, allowedPartIds) => {
  const values = normalizeWizardSelectedValues(wizardSelectedValues);

  if (values.length === 0) return false;
  if (!Array.isArray(allowedPartIds) || allowedPartIds.length === 0) return true;

  const allowed = new Set(allowedPartIds);
  return values.some(v => allowed.has(v?.partId));
};

/**
 * Determines if button should be removed or disabled.
 * - Remove if no allNavItems found for deps
 * - Disable if allNavItems exist but no rows
 * - If wizard is active and rows exist, but wizard does not include any deps -> disable not_available
 */
const getButtonAvailability = ({
                                 featuresData,
                                 deps,
                                 wizardIsActive,
                                 wizardSelectedValues
                               }) => {
  const depKeys = Array.isArray(deps) ? deps : [];

  const items = depKeys
    .map(key => featuresData?.[key])
    .filter(Boolean);

  const hasAnyAllNavItems = items.some(item => item?.allNavItems?.length > 0);
  if (!hasAnyAllNavItems) {
    return { shouldRemove: true, isDisabled: false, disableReason: "not_available" };
  }

  const hasAnyRows = items.some(item => item?.rows?.length > 0);
  if (!hasAnyRows) {
    const wasFilteredByAR3D = items.some(item => item?.wasFilteredByAR3D);
    return {
      shouldRemove: false,
      isDisabled: true,
      disableReason: wasFilteredByAR3D ? "missing_3d_models" : "not_available",
    };
  }

  if (wizardIsActive) {
    const match = hasWizardMatch(wizardSelectedValues, depKeys);
    if (!match) {
      return { shouldRemove: false, isDisabled: true, disableReason: "not_available" };
    }
  }

  return { shouldRemove: false, isDisabled: false, disableReason: "not_available" };
};

const SqftToolMenuCabinet = (props) => {
  const {
    activeButton,
    menuOpened,
    setSQFTActiveButton,
    setSQFTMenuOpened,
    fabricatorData,
    intl,
    showSqftTooltip,
    hideSqftTooltip,
    wizardSelectedValues,
    wizardIsActive
  } = props;

  let buttons = getIsBathroom()
    ? is3DMode()
      ? { ...SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS }
      : { ...SIDEBAR_NAVIGATION_BATHROOM_CABINET_BUTTONS }
    : is3DMode()
      ? { ...SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS }
      : { ...SIDEBAR_NAVIGATION_CABINET_BUTTONS };

  const expanded = activeButton
    && menuOpened !== "bumpout"
    && (menuOpened === "move" || Object.keys(buttons).some(buttonKey => menuOpened === buttonKey));

  const partIds = useMemo(() => CABINET_MENU_FETCH_KEYS, []);
  const { featuresData, isLoading } = useFetchFeatures(partIds, "", true);

  // Stores per-button state
  const buttonStates = {};
  const buttonDisableReasons = {};

  if (!isLoading && featuresData) {
    buttons = Object.keys(buttons).reduce((newButtons, buttonKey) => {
      let shouldRemove = false;
      let isDisabled = false;
      let disableReason = "not_available";

      const deps = CABINET_MENU_BUTTON_DEPS[buttonKey];
      if (deps?.length) {
        const availability = getButtonAvailability({
          featuresData,
          deps,
          wizardIsActive,
          wizardSelectedValues
        });

        shouldRemove = availability.shouldRemove;
        isDisabled = availability.isDisabled;
        disableReason = availability.disableReason;
      }

      if (shouldRemove) {
        return newButtons;
      }

      buttonStates[buttonKey] = isDisabled;
      buttonDisableReasons[buttonKey] = disableReason;
      newButtons[buttonKey] = buttons[buttonKey];

      return newButtons;
    }, {});
  }

  const handleButtonClick = (buttonKey) => {
    if (buttonStates[buttonKey]) return;

    setSQFTMenuOpened(buttonKey);
    setSQFTActiveButton(buttonKey);
    config.workspace.menuItemActive = buttonKey;
    resetEvents();

    switch (buttonKey) {
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.move:
        setSQFTMenuOpened('');
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.resize:
        setSQFTMenuOpened('');
        setSQFTActiveButton(buttonKey);
        handleResizeClick();
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.rotate:
        handleRotateClick();
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.spacer:
        setSQFTMenuOpened('');
        setSQFTActiveButton(buttonKey);
        handleSpacerClick();
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.wall:
        setSQFTActiveButton(buttonKey);
        handleWallClick();
        break;
      case 'reset':
        handleResetClick();
        break;
    }

    $('.lightbox-option-selected').removeClass('lightbox-option-selected');
  };

  const handleMouseEnter = (event, buttonKey) => {
    if (!buttonStates[buttonKey]) return;

    const targetRect = event.currentTarget.getBoundingClientRect();
    const featureName = getButtonName(buttonKey);
    const reason = buttonDisableReasons[buttonKey];

    const titleId = reason === "missing_3d_models"
      ? "sqft.navigation.missing_3d_models_title"
      : "sqft.navigation.not_available_title";

    const descId = reason === "missing_3d_models"
      ? "sqft.navigation.missing_3d_models_description"
      : "sqft.navigation.not_available_description";

    showSqftTooltip({
      title: `${featureName} ${intl.formatMessage({ id: titleId })}`,
      description: `${featureName} ${intl.formatMessage({ id: descId })}`,
      icon: <AutoFixHighIcon />,
      position: 'right',
      targetRect
    });
  };

  const handleMouseLeave = () => {
    hideSqftTooltip();
  };

  const getButtonName = (buttonKey) => {
    const layout = getCurrentLayout();
    const featureGroup = layout.getFeatureGroupByPartId(buttonKey);
    if (featureGroup) {
      return getFeatureGroupName(featureGroup, intl, fabricatorData);
    }
    return intl.formatMessage({ id: "sqft.navigation.submenu." + buttonKey });
  };

  return (
    <nav className={"navigation side" + (expanded ? " expanded" : "")} data-side="navigation">
      <div className="navigation-list">
        {isLoading
          ? <LoadingSpinner />
          : !expanded ? null : Object.keys(buttons).map((buttonKey) => {
            const isDisabled = buttonStates[buttonKey];

            return (
              <button
                key={buttonKey}
                className={
                  "animated fadeInUp button button--" + buttonKey +
                  (isDisabled ? " button--disabled" : "")
                }
                onClick={() => handleButtonClick(buttonKey)}
                onMouseEnter={(e) => handleMouseEnter(e, buttonKey)}
                onMouseLeave={handleMouseLeave}
              >
                <span className="button-text">
                  {getButtonName(buttonKey)}
                </span>
              </button>
            );
          })}
      </div>
    </nav>
  );
};

const mapStateToProps = (store) => ({
  menuOpened: store.sqftMenuState.menuOpened,
  activeButton: store.sqftMenuState.activeButton,
  fabricatorData: store.fabricatorState,
  wizardSelectedValues: store.wizardState.wizardSelectedValues,
  wizardIsActive: store.wizardState.wizardIsActive
});

const mapDispatchToProps = {
  setSQFTMenuOpened,
  setSQFTActiveButton,
  addChangeValuePopup,
  showSqftTooltip,
  hideSqftTooltip,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(SqftToolMenuCabinet)
);
